import {
	getFormProps,
	getInputProps,
	getTextareaProps,
	useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { invariantResponse } from '@epic-web/invariant'
import { parseFormData } from '@mjackson/form-data-parser'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { motion } from 'framer-motion'
import { Img } from 'openimg/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { data, Form, Link } from 'react-router'
import { serverOnly$ } from 'vite-env-only/macros'
import { z } from 'zod'
import { ConfirmationDialog } from '#app/components/confirmation-dialog.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList } from '#app/components/forms.tsx'
import { InquiryDialog } from '#app/components/inquiry-dialog.tsx'
import { RichContent } from '#app/components/rich-content.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Input } from '#app/components/ui/input.tsx'
import { Label } from '#app/components/ui/label.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { Textarea } from '#app/components/ui/textarea.tsx'
import { fadeIn, staggerContainer, staggerItem } from '#app/utils/animations.ts'
import { getUserId } from '#app/utils/auth.server.ts'
import {
	deleteBeeld,
	deleteBeeldImage,
	reorderBeeldImage,
	updateBeeld,
	uploadBeeldImage,
} from '#app/utils/beeld-mutations.server.ts'
import { cachified, lruCache } from '#app/utils/cache.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { pipeHeaders } from '#app/utils/headers.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import {
	getNoteImgSrc,
	imageDisplaySize,
	useIsPending,
} from '#app/utils/misc.tsx'
import {
	buildMeta,
	buildCreativeWorkJsonLd,
	buildBreadcrumbJsonLd,
	metaDescriptionFromContent,
} from '#app/utils/seo.ts'
import { titleToSlug } from '#app/utils/slug.ts'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import { useOptionalUser } from '#app/utils/user.ts'
import { type Route } from './+types/beelden_.$title.ts'

const DeleteNoteSchema = z.object({
	intent: z.literal('delete-note'),
	noteId: z.string(),
})

const DeleteImageSchema = z.object({
	intent: z.literal('delete-image'),
	imageId: z.string(),
})

const EditNoteSchema = z.object({
	intent: z.literal('edit-note'),
	noteId: z.string(),
	title: z.string().min(1, 'Title is required'),
	content: z.string(),
	category: z
		.enum(['autonoom', 'drijvend', 'openbare_ruimte'])
		.default('autonoom'),
})

const ReorderImageSchema = z.object({
	intent: z.literal('reorder-image'),
	imageId: z.string(),
	direction: z.enum(['up', 'down']),
})

const UploadImageSchema = z.object({
	intent: z.literal('upload-image'),
	noteId: z.string(),
	image: z
		.instanceof(File)
		.refine((file) => file.size > 0, 'Image is required')
		.refine(
			(file) => file.size <= 3 * 1024 * 1024, // 3MB
			'Image size must be less than 3MB',
		)
		.refine((file) => file.type.startsWith('image/'), 'File must be an image'),
	altText: z.string().optional(),
})

const InquirySchema = z.object({
	intent: z.literal('submit-inquiry'),
	noteId: z.string(),
	noteTitle: z.string(),
	firstName: z.string().min(1, 'First name is required'),
	lastName: z.string().min(1, 'Last name is required'),
	email: z.string().email('Please enter a valid email'),
	phone: z.string().optional(),
	message: z.string().min(10, 'Please enter at least 10 characters'),
	hasRingnalda: z.enum(['yes', 'no']),
})

export const handle: SEOHandle & { i18n: string } = {
	i18n: 'common',
	// Add every sculpture detail page to sitemap.xml. serverOnly$ keeps the
	// prisma reference out of the client bundle (handle is a shared export).
	getSitemapEntries: serverOnly$(async () => {
		const notes = await prisma.note.findMany({
			select: { title: true },
			where: { images: { some: {} } },
		})
		return notes.map((note) => ({
			route: `/beelden/${titleToSlug(note.title)}`,
			priority: 0.7 as const,
		}))
	}),
}

/**
 * Cached slug→noteId index covering every sculpture with at least one image.
 * One small query (only id+title) feeds every detail-page lookup, and an
 * O(1) lookup beats running `contains` + multi-image join per request.
 */
async function getBeeldSlugIndex() {
	return cachified({
		key: 'beelden-slug-index',
		cache: lruCache,
		ttl: 1000 * 60 * 5,
		staleWhileRevalidate: 1000 * 60 * 60,
		async getFreshValue() {
			const all = await prisma.note.findMany({
				select: { id: true, title: true },
				where: { images: { some: {} } },
			})
			const map: Record<string, string> = {}
			for (const n of all) {
				map[titleToSlug(n.title)] = n.id
			}
			return map
		},
	})
}

async function getBeeldBySlug(slug: string, language: string) {
	const slugIndex = await getBeeldSlugIndex()
	let noteId = slugIndex[slug]
	if (!noteId) {
		// The slug index is cached (5 min), so a sculpture created since the last
		// refresh — via the editor, or on another instance — won't be in it yet.
		// Fall back to a direct lookup so an existing sculpture is never a false
		// 404 ("Beeld niet gevonden").
		const candidates = await prisma.note.findMany({
			select: { id: true, title: true },
			where: { images: { some: {} } },
		})
		noteId = candidates.find((n) => titleToSlug(n.title) === slug)?.id
	}
	if (!noteId) return null

	return cachified({
		key: `beelden-detail-${noteId}-${language}`,
		cache: lruCache,
		ttl: 1000 * 60 * 5,
		staleWhileRevalidate: 1000 * 60 * 60,
		async getFreshValue() {
			const full = await prisma.note.findUnique({
				select: {
					id: true,
					title: true,
					content: true,
					category: true,
					owner: { select: { username: true, name: true } },
					translations: {
						where: { language },
						select: { title: true, content: true },
					},
					images: {
						select: {
							id: true,
							objectKey: true,
							altText: true,
							width: true,
							height: true,
						},
						orderBy: { createdAt: 'asc' },
					},
				},
				where: { id: noteId },
			})
			if (!full) return null
			return {
				id: full.id,
				title: full.translations[0]?.title || full.title,
				content: full.translations[0]?.content || full.content,
				category: full.category,
				owner: full.owner,
				images: full.images,
			}
		},
	})
}

export async function loader({ params, request }: Route.LoaderArgs) {
	const { title: slug } = params

	const { getLocale } = await import('#app/i18n.server.ts')
	const language = await getLocale(request)

	const note = await getBeeldBySlug(slug, language)
	invariantResponse(note, 'Beeld niet gevonden', { status: 404 })

	return data(
		{ note },
		{
			headers: {
				'Cache-Control':
					'public, max-age=60, s-maxage=300, stale-while-revalidate=3600',
			},
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function GenerateContentFromImages(images: Array<{ altText: string | null }>) {
	const descriptions = images
		.filter((img) => img.altText)
		.map((img) => img.altText)

	if (descriptions.length === 0) {
		return (
			'Dit beeldhouwwerk bestaat uit ' +
			images.length +
			' afbeelding' +
			(images.length === 1 ? '' : 'en') +
			'.'
		)
	}

	// Create a summary from the image descriptions
	const summary = descriptions.join(' | ')
	return `Dit beeldhouwwerk bestaat uit ${images.length} afbeelding${images.length === 1 ? '' : 'en'}. ${summary}`
}

export async function action({ request }: Route.ActionArgs) {
	const userId = await getUserId(request)

	const contentType = request.headers.get('content-type')
	const isMultipart = contentType?.includes('multipart/form-data')

	const formData = isMultipart
		? await parseFormData(request, { maxFileSize: 3 * 1024 * 1024 })
		: await request.formData()

	const intent = formData.get('intent')

	// The inquiry form is public — any visitor can request purchase info.
	// Every other intent mutates sculpture data and requires a logged-in user.
	if (intent !== 'submit-inquiry' && !userId) {
		throw data({ error: 'Unauthorized' }, { status: 401 })
	}

	if (intent === 'delete-note') {
		const submission = parseWithZod(formData, {
			schema: DeleteNoteSchema,
		})

		if (submission.status !== 'success') {
			return data(
				{ result: submission.reply() },
				{ status: submission.status === 'error' ? 400 : 200 },
			)
		}

		const { noteId } = submission.value

		const note = await prisma.note.findFirst({
			select: { id: true, title: true },
			where: { id: noteId },
		})
		invariantResponse(note, 'Beeld niet gevonden', { status: 404 })

		// Allow any logged-in user to delete any note
		await deleteBeeld(noteId)

		return redirectWithToast('/beelden', {
			type: 'success',
			title: 'Succes',
			description: `"${note.title}" is verwijderd.`,
		})
	}

	if (intent === 'delete-image') {
		const submission = parseWithZod(formData, {
			schema: DeleteImageSchema,
		})

		if (submission.status !== 'success') {
			return data(
				{ result: submission.reply() },
				{ status: submission.status === 'error' ? 400 : 200 },
			)
		}

		const { imageId } = submission.value

		const image = await prisma.noteImage.findFirst({
			select: { id: true, noteId: true },
			where: { id: imageId },
		})
		invariantResponse(image, 'Afbeelding niet gevonden', { status: 404 })

		// Allow any logged-in user to delete any image
		await deleteBeeldImage({ noteId: image.noteId, imageId })

		return data(
			{
				result: { status: 'success' as const },
				message: 'Afbeelding succesvol verwijderd',
			},
			{ status: 200 },
		)
	}

	if (intent === 'edit-note') {
		const submission = parseWithZod(formData, {
			schema: EditNoteSchema,
		})

		if (submission.status !== 'success') {
			return data(
				{ result: submission.reply() },
				{ status: submission.status === 'error' ? 400 : 200 },
			)
		}

		const { noteId, title, content, category } = submission.value

		const note = await prisma.note.findFirst({
			select: { id: true },
			where: { id: noteId },
		})
		invariantResponse(note, 'Beeld niet gevonden', { status: 404 })

		await updateBeeld({ noteId, title, content, category })

		return data(
			{
				result: { status: 'success' as const },
				message: 'Beeld succesvol bijgewerkt',
			},
			{ status: 200 },
		)
	}

	if (intent === 'reorder-image') {
		const submission = parseWithZod(formData, {
			schema: ReorderImageSchema,
		})

		if (submission.status !== 'success') {
			return data(
				{ result: submission.reply() },
				{ status: submission.status === 'error' ? 400 : 200 },
			)
		}

		const { imageId, direction } = submission.value

		const currentImage = await prisma.noteImage.findFirst({
			select: { id: true, noteId: true },
			where: { id: imageId },
		})
		invariantResponse(currentImage, 'Afbeelding niet gevonden', { status: 404 })

		const { moved } = await reorderBeeldImage({
			noteId: currentImage.noteId,
			imageId,
			direction,
		})

		return data(
			{
				result: { status: 'success' as const },
				message: moved
					? 'Volgorde van afbeelding bijgewerkt'
					: 'Volgorde van afbeelding ongewijzigd',
			},
			{ status: 200 },
		)
	}

	if (intent === 'upload-image') {
		const submission = parseWithZod(formData, {
			schema: UploadImageSchema,
		})

		if (submission.status !== 'success') {
			return data(
				{ result: submission.reply() },
				{ status: submission.status === 'error' ? 400 : 200 },
			)
		}

		const { noteId, image, altText } = submission.value

		const note = await prisma.note.findFirst({
			select: { id: true },
			where: { id: noteId },
		})
		invariantResponse(note, 'Beeld niet gevonden', { status: 404 })
		invariantResponse(userId, 'Unauthorized', { status: 401 })

		// Upload image and create database record
		await uploadBeeldImage({ userId, noteId, file: image, altText })

		return data(
			{
				result: { status: 'success' as const },
				message: 'Afbeelding succesvol geüpload',
			},
			{ status: 200 },
		)
	}

	if (intent === 'submit-inquiry') {
		// This intent is open to anonymous visitors, so it needs spam
		// protection. The authenticated intents above are already gated by
		// the session check and must not be affected by the honeypot.
		await checkHoneypot(formData)

		const submission = parseWithZod(formData, {
			schema: InquirySchema,
		})

		if (submission.status !== 'success') {
			return data(
				{ result: submission.reply() },
				{ status: submission.status === 'error' ? 400 : 200 },
			)
		}

		const {
			noteId,
			noteTitle,
			firstName,
			lastName,
			email,
			phone,
			message,
			hasRingnalda,
		} = submission.value

		// Create inquiry in database
		await prisma.inquiry.create({
			data: {
				firstName,
				lastName,
				email,
				phone: phone || null,
				message,
				hasRingnalda: hasRingnalda === 'yes',
				noteId,
				noteTitle,
			},
		})

		// Send emails
		const { sendInquiryEmails } = await import('#app/utils/email.server.tsx')
		await sendInquiryEmails({
			inquiry: {
				firstName,
				lastName,
				email,
				phone: phone || null,
				message,
				hasRingnalda: hasRingnalda === 'yes',
				noteTitle,
			},
		})

		return redirectWithToast('/', {
			type: 'success',
			title: 'Succes',
			description: 'Uw aanvraag is verzonden. We nemen snel contact met u op.',
		})
	}

	throw data({ error: 'Invalid intent' }, { status: 400 })
}

export default function NoteDetailRoute({
	loaderData,
	actionData,
}: Route.ComponentProps) {
	const { note } = loaderData
	const { t } = useTranslation()
	const user = useOptionalUser()
	const canEdit = Boolean(user)
	const [isEditing, setIsEditing] = useState(false)
	const [showUploadForm, setShowUploadForm] = useState(false)
	return (
		<motion.div
			className="pt-[120px] pb-20"
			initial="initial"
			animate="animate"
			variants={fadeIn}
		>
			<div className="container">
				{/* Back link */}
				<Link
					to="/beelden"
					className="btn-gallery-link mb-12 inline-flex"
					viewTransition
				>
					<Icon name="arrow-left" className="h-4 w-4" /> Alle werken
				</Link>

				{/* Two-column layout */}
				<div
					className="grid items-start gap-20"
					style={{ gridTemplateColumns: '1.6fr 1fr' }}
				>
					{/* Left column — Images */}
					<motion.div
						className="flex flex-col gap-6"
						data-testid="detail-left-column"
						initial="initial"
						whileInView="animate"
						viewport={{ once: true, amount: 'some' }}
						variants={staggerContainer}
					>
						{note.images.map((image, index) => {
							const display = imageDisplaySize(image, 800)
							return (
								<motion.div
									key={image.id}
									variants={staggerItem}
									className="group relative overflow-hidden"
									data-testid="images-container"
								>
									{canEdit && (
										<div className="absolute top-2 right-2 z-10 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100">
											<ImageControls
												imageId={image.id}
												isFirst={index === 0}
												isLast={index === note.images.length - 1}
											/>
										</div>
									)}
									<Img
										src={getNoteImgSrc(image.objectKey)}
										alt={image.altText ?? `${note.title} - ${index + 1}`}
										className="h-auto w-full object-contain"
										width={display.width}
										height={display.height}
										fit={display.fit}
										isAboveFold={index === 0}
									/>
								</motion.div>
							)
						})}
					</motion.div>

					{/* Right column — Sticky aside */}
					<aside
						className="lg:sticky lg:top-[120px]"
						data-testid="detail-right-column"
					>
						{canEdit && !isEditing && (
							<div className="mb-6 flex items-center gap-2">
								<Button
									variant="outline"
									size="sm"
									onClick={() => setIsEditing(!isEditing)}
								>
									<Icon name="pencil-2" className="mr-2 h-4 w-4" />
									Bewerken
								</Button>
								<DeleteNoteButton noteId={note.id} noteTitle={note.title} />
							</div>
						)}

						{isEditing ? (
							<EditNoteForm
								note={note}
								onCancel={() => setIsEditing(false)}
								actionData={actionData}
							/>
						) : (
							<>
								<div className="eyebrow mb-3">
									{note.category === 'autonoom' && t('categoryAutonomous')}
									{note.category === 'drijvend' && t('categoryFloating')}
									{note.category === 'openbare_ruimte' &&
										t('categoryPublicSpace')}
								</div>
								<h1
									className="display mb-6"
									style={{ fontSize: 'clamp(36px, 4vw, 56px)' }}
								>
									{note.title}
								</h1>
								<div
									className="mb-8 text-base leading-relaxed"
									style={{ color: 'var(--ink-soft)' }}
								>
									{note.content ? (
										<RichContent html={note.content} />
									) : note.images.length > 0 ? (
										GenerateContentFromImages(note.images)
									) : null}
								</div>

								{/* Metadata */}
								<dl
									className="mb-10 grid gap-x-6 gap-y-3 text-sm"
									style={{ gridTemplateColumns: 'auto 1fr' }}
								>
									<dt className="eyebrow">{t('imagesLabel')}</dt>
									<dd>{note.images.length}</dd>
									<dt className="eyebrow">{t('categoryLabel')}</dt>
									<dd>
										{note.category === 'autonoom' && t('categoryAutonomous')}
										{note.category === 'drijvend' && t('categoryFloating')}
										{note.category === 'openbare_ruimte' &&
											t('categoryPublicSpace')}
									</dd>
								</dl>

								{/* Enquire CTA */}
								<InquiryDialog
									noteId={note.id}
									noteTitle={note.title}
									actionData={actionData}
								/>

								<p
									className="mt-6 text-xs leading-relaxed"
									style={{ color: 'var(--muted-gallery)' }}
								>
									{t('shippingNote')}
								</p>
							</>
						)}

						{/* Upload */}
						{canEdit && !isEditing && (
							<div
								className="mt-8 border-t pt-8"
								style={{ borderColor: 'var(--line)' }}
							>
								<Button
									variant="outline"
									onClick={() => setShowUploadForm(!showUploadForm)}
									className="mb-4"
								>
									<Icon name="plus" className="mr-2 h-4 w-4" />
									{showUploadForm ? 'Upload annuleren' : 'Afbeelding toevoegen'}
								</Button>
								{showUploadForm && (
									<UploadImageForm
										noteId={note.id}
										onSuccess={() => setShowUploadForm(false)}
										actionData={actionData}
									/>
								)}
							</div>
						)}
					</aside>
				</div>
			</div>

			{/* Responsive override for mobile */}
			<style>{`@media (max-width: 900px) {
				[data-testid="detail-left-column"] { order: 1; }
				.container > div[style*="1.6fr"] { grid-template-columns: 1fr !important; gap: 48px !important; }
				.container > div[style*="1.6fr"] aside { position: static !important; }
			}`}</style>
		</motion.div>
	)
}

function DeleteNoteButton({
	noteId,
	noteTitle,
}: {
	noteId: string
	noteTitle: string
}) {
	return (
		<ConfirmationDialog
			trigger={
				<Button variant="destructive" size="sm">
					<Icon name="trash" className="mr-2 h-4 w-4" />
					Beeld verwijderen
				</Button>
			}
			title="Beeld verwijderen"
			description={`Weet je zeker dat je "${noteTitle}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.`}
			confirmText="Verwijderen"
			formData={{
				intent: 'delete-note',
				noteId: noteId,
			}}
		/>
	)
}

function ImageControls({
	imageId,
	isFirst,
	isLast,
}: {
	imageId: string
	isFirst: boolean
	isLast: boolean
}) {
	return (
		<div className="flex gap-1">
			{!isFirst && (
				<Form method="POST">
					<input type="hidden" name="intent" value="reorder-image" />
					<input type="hidden" name="imageId" value={imageId} />
					<input type="hidden" name="direction" value="up" />
					<Button
						type="submit"
						variant="secondary"
						size="sm"
						className="h-8 w-8 p-0 shadow-lg"
					>
						<Icon name="arrow-up" className="h-4 w-4" />
						<span className="sr-only">Omhoog verplaatsen</span>
					</Button>
				</Form>
			)}
			{!isLast && (
				<Form method="POST">
					<input type="hidden" name="intent" value="reorder-image" />
					<input type="hidden" name="imageId" value={imageId} />
					<input type="hidden" name="direction" value="down" />
					<Button
						type="submit"
						variant="secondary"
						size="sm"
						className="h-8 w-8 p-0 shadow-lg"
					>
						<Icon name="arrow-down" className="h-4 w-4" />
						<span className="sr-only">Omlaag verplaatsen</span>
					</Button>
				</Form>
			)}
			<DeleteImageButton imageId={imageId} />
		</div>
	)
}

function DeleteImageButton({ imageId }: { imageId: string }) {
	return (
		<ConfirmationDialog
			trigger={
				<Button
					variant="destructive"
					size="sm"
					className="h-8 w-8 p-0 shadow-lg"
				>
					<Icon name="trash" className="h-4 w-4" />
					<span className="sr-only">Afbeelding verwijderen</span>
				</Button>
			}
			title="Afbeelding verwijderen"
			description="Weet je zeker dat je deze afbeelding wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt."
			confirmText="Verwijderen"
			formData={{
				intent: 'delete-image',
				imageId: imageId,
			}}
		/>
	)
}

function EditNoteForm({
	note,
	onCancel,
	actionData,
}: {
	note: { id: string; title: string; content: string; category?: string | null }
	onCancel: () => void
	actionData?: any
}) {
	const isPending = useIsPending()
	const [form, fields] = useForm({
		id: 'edit-note',
		constraint: getZodConstraint(EditNoteSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: EditNoteSchema })
		},
		defaultValue: {
			title: note.title,
			content: note.content,
			category: note.category || 'autonoom',
		},
	})

	return (
		<Form method="POST" {...getFormProps(form)} className="space-y-6">
			<input type="hidden" name="intent" value="edit-note" />
			<input type="hidden" name="noteId" value={note.id} />

			<div>
				<Label htmlFor={fields.title.id}>Titel</Label>
				<Input
					{...getInputProps(fields.title, { type: 'text' })}
					className="font-heading text-2xl font-bold md:text-3xl lg:text-4xl"
				/>
				<div className="min-h-[32px] px-4 pt-1 pb-3">
					<ErrorList id={fields.title.errorId} errors={fields.title.errors} />
				</div>
			</div>

			<div>
				<Label htmlFor={fields.category.id}>Categorie</Label>
				<select
					id={fields.category.id}
					name={fields.category.name}
					defaultValue={fields.category.initialValue ?? 'autonoom'}
					className="w-full border-0 border-b px-0 py-2.5 text-base outline-none"
					style={{
						borderColor: 'var(--line)',
						background: 'var(--paper)',
						fontFamily: 'var(--font-sans)',
						color: 'var(--ink)',
					}}
				>
					<option value="autonoom">Autonoom</option>
					<option value="drijvend">Drijvend</option>
					<option value="openbare_ruimte">Openbare ruimte</option>
				</select>
				<div className="min-h-[32px] px-4 pt-1 pb-3">
					<ErrorList
						id={fields.category.errorId}
						errors={fields.category.errors}
					/>
				</div>
			</div>

			<div>
				<Label htmlFor={fields.content.id}>Beschrijving</Label>
				<Textarea
					{...getTextareaProps(fields.content)}
					className="resize-vertical min-h-[200px]"
				/>
				<div className="min-h-[32px] px-4 pt-1 pb-3">
					<ErrorList
						id={fields.content.errorId}
						errors={fields.content.errors}
					/>
				</div>
			</div>

			<div className="flex gap-4">
				<StatusButton
					type="submit"
					disabled={isPending}
					status={isPending ? 'pending' : 'idle'}
				>
					Wijzigingen opslaan
				</StatusButton>
				<Button type="button" variant="outline" onClick={onCancel}>
					Annuleren
				</Button>
			</div>

			<ErrorList id={form.errorId} errors={form.errors} />
		</Form>
	)
}

function UploadImageForm({
	noteId,
	onSuccess,
	actionData,
}: {
	noteId: string
	onSuccess: () => void
	actionData?: any
}) {
	const isPending = useIsPending()
	const [form, fields] = useForm({
		id: 'upload-image',
		constraint: getZodConstraint(UploadImageSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: UploadImageSchema })
		},
		onSubmit(event, { submission }) {
			if (submission?.status === 'success') {
				onSuccess()
			}
		},
	})

	return (
		<Form
			method="POST"
			{...getFormProps(form)}
			className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700"
			encType="multipart/form-data"
		>
			<input type="hidden" name="intent" value="upload-image" />
			<input type="hidden" name="noteId" value={noteId} />

			<div>
				<Label htmlFor={fields.image.id}>Afbeeldingsbestand</Label>
				<Input
					{...getInputProps(fields.image, { type: 'file' })}
					accept="image/*"
				/>
				<div className="min-h-[32px] px-4 pt-1 pb-3">
					<ErrorList id={fields.image.errorId} errors={fields.image.errors} />
				</div>
			</div>

			<div>
				<Label htmlFor={fields.altText.id}>Alt-tekst (optioneel)</Label>
				<Input
					{...getInputProps(fields.altText, { type: 'text' })}
					placeholder="Beschrijf de afbeelding..."
				/>
				<div className="min-h-[32px] px-4 pt-1 pb-3">
					<ErrorList
						id={fields.altText.errorId}
						errors={fields.altText.errors}
					/>
				</div>
			</div>

			<div className="flex gap-2">
				<StatusButton
					type="submit"
					disabled={isPending}
					status={isPending ? 'pending' : 'idle'}
				>
					Afbeelding uploaden
				</StatusButton>
			</div>

			<ErrorList id={form.errorId} errors={form.errors} />
		</Form>
	)
}

export const meta: Route.MetaFunction = ({ data, params }) => {
	const note = data?.note
	if (!note) return [{ title: 'Beeld niet gevonden' }]

	// The route param IS the canonical slug (titleToSlug of the base title) —
	// note.title can be a translation, so never rebuild the URL from it.
	const notePath = `/beelden/${params.title}`

	const firstImage = note.images[0]
	const imageUrl = firstImage
		? `/resources/images?objectKey=${encodeURIComponent(firstImage.objectKey)}&w=1200&format=webp`
		: null

	return [
		...buildMeta({
			title: note.title,
			description: `"${note.title}" — sculptuur van beeldend kunstenaar Else Ringnalda.`,
			url: notePath,
			image: imageUrl,
			imageAlt: firstImage?.altText || note.title,
		}),
		buildCreativeWorkJsonLd({
			title: note.title,
			description: metaDescriptionFromContent(note.content),
			url: notePath,
			image: imageUrl,
		}),
		buildBreadcrumbJsonLd([
			{ name: 'Home', url: '/' },
			{ name: 'Werken', url: '/beelden' },
			{ name: note.title, url: notePath },
		]),
	]
}

export function ErrorBoundary() {
	const { t } = useTranslation()
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: () => (
					<div className="flex min-h-screen flex-col items-center justify-center pt-[100px]">
						<div className="text-center">
							<h1 className="mb-4 text-4xl font-bold text-gray-900 dark:text-white">
								{t('sculptureNotFound')}
							</h1>
							<p className="mb-8 text-lg text-gray-600 dark:text-gray-400">
								{t('sculptureNotFoundText')}
							</p>
							<Button asChild>
								<Link to="/beelden">
									<Icon name="arrow-left">{t('backToOverview')}</Icon>
								</Link>
							</Button>
						</div>
					</div>
				),
			}}
		/>
	)
}
